// The opt-in reveal's rules outside React (spec 6.10.18): the confirmation's session choice, the learned
// "reason required" switch, error texts, the activity entry reference and the clipboard write. Nothing here
// keeps a secret value: values only pass through `writeClipboard`'s promise.
import { createContext, useContext } from 'react';
import { ApiError, isApiError } from '../api/client';
import type { SettingLocation } from '../api/types';
import { describeError } from '../components/toast';
import { strings } from '../strings';

const r = strings.reveal;

/** Seconds a revealed value stays shown. */
export const REVEAL_SECONDS = 30;
/** Characters of a value the cell shows; longer values get Expand. */
export const INLINE_CHARACTERS = 24;
export const REASON_MAX = 200;

const STORAGE_PREFIX = 'nebisys.reveal.';
const skipKey = (user: string) => `${STORAGE_PREFIX}skipConfirm:${user}`;

/** Learned from a 400 `FIELD_REQUIRED` at `/reason`; kept for the life of the page (never stored). */
let reasonRequired = false;

export function isReasonRequired(): boolean {
  return reasonRequired;
}

/** The server requires a reason: ask every time, and forget "Don't ask again". */
export function markReasonRequired(user: string) {
  reasonRequired = true;
  try {
    window.sessionStorage.removeItem(skipKey(user));
  } catch {
    // Storage may be unavailable; the flag above is what counts.
  }
}

/** Whether the confirmation comes before this reveal or copy. */
export function needsConfirmation(user: string): boolean {
  if (reasonRequired) return true;
  try {
    return window.sessionStorage.getItem(skipKey(user)) !== '1';
  } catch {
    return true;
  }
}

/** "Don't ask again until I sign out" (never a value: only this flag is stored). */
export function skipConfirmation(user: string) {
  if (reasonRequired) return;
  try {
    window.sessionStorage.setItem(skipKey(user), '1');
  } catch {
    // Without storage the confirmation simply comes again.
  }
}

/** Sign-out: every reveal choice is forgotten. */
export function forgetRevealChoices() {
  try {
    const storage = window.sessionStorage;
    for (let i = storage.length - 1; i >= 0; i--) {
      const key = storage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) storage.removeItem(key);
    }
  } catch {
    // Nothing to forget without storage.
  }
}

const signOutListeners = new Set<() => void>();

/** Sign-out as a hide trigger of its own (spec 6.10.18): every revealed value hides at once. */
export function onRevealSignOut(listener: () => void): () => void {
  signOutListeners.add(listener);
  return () => signOutListeners.delete(listener);
}

/**
 * Ends the reveal session on sign-out, before any redirect: revealed values hide synchronously (a redirect that
 * fails or never happens can't leave one on a signed-out page) and the confirmation choices are forgotten.
 */
export function endRevealSession() {
  for (const listener of [...signOutListeners]) listener();
  forgetRevealChoices();
}

/** Tests only. */
export function resetRevealSession() {
  reasonRequired = false;
  forgetRevealChoices();
}

/** The reason as sent: trimmed, an empty one as null. */
export function reasonToSend(reason: string): string | null {
  const trimmed = reason.trim();
  return trimmed ? trimmed : null;
}

/** A 400 whose diagnostics say the reason is required. */
export function isReasonRequiredError(error: unknown): boolean {
  return (
    isApiError(error) &&
    error.status === 400 &&
    error.diagnostics.some((d) => d.code === 'FIELD_REQUIRED' && d.path === '/reason')
  );
}

/** Messages of a 400 for the confirmation's reason field. */
export function reasonProblems(error: unknown): string[] {
  if (!isApiError(error) || error.status !== 400) return [];
  if (isReasonRequiredError(error)) return [r.reasonMissing];
  const messages = error.diagnostics.map((d) => d.message).filter(Boolean);
  return messages.length ? messages : [error.detail ?? strings.common.unknownError];
}

/** The 429's text: the seconds from `Retry-After` when the server sent them (a rolling minute, spec 4.4). */
function rateLimitedText(error: ApiError): string {
  const seconds = error.retryAfterSeconds;
  return seconds !== null && seconds > 0 && seconds < 60
    ? r.errors.rateLimitedFor(seconds)
    : r.errors.rateLimited;
}

/**
 * What a failed reveal shows: a short badge text and its explanation. Only Problem Details are shown; any other
 * error (an unreadable 200 body, whose parser message could quote the value) gets a fixed text (spec 6.10.18).
 */
export function revealFailure(error: unknown): { short: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.code === 'AUDIT_UNAVAILABLE') return { short: r.errors.auditShort, detail: r.errors.audit };
    if (error.status === 429) {
      const text = rateLimitedText(error);
      return { short: text, detail: text };
    }
    if (error.code === 'REVEAL_DISABLED') return { short: r.errors.disabled, detail: r.errors.disabled };
    if (error.status === 403) return { short: r.errors.forbidden, detail: r.errors.forbidden };
    const described = describeError(error);
    const text = described.detail ?? described.title;
    return { short: text, detail: text };
  }
  return { short: r.errors.unexpectedShort, detail: r.errors.unexpected };
}

/** Marks the clipboard being unavailable (its fixed text may be shown). */
export class ClipboardUnavailableError extends Error {
  constructor() {
    super(r.noClipboard);
    this.name = 'ClipboardUnavailableError';
  }
}

/** A toast for a failed copy: the same texts; the browser's own clipboard errors get a fixed text too. */
export function copyFailure(error: unknown): { title: string; detail?: string } {
  if (error instanceof ApiError) return { title: r.copyFailed, detail: revealFailure(error).detail };
  if (error instanceof ClipboardUnavailableError) return { title: r.copyFailed, detail: r.noClipboard };
  return { title: r.copyFailed, detail: r.errors.copyUnexpected };
}

export { entryReference } from '../activity/describe';

/** The roles named in the missing-role reason: `/me` roles without the implied `reader` when there are others. */
export function rolesText(roles: readonly string[]): string {
  const shown = roles.length > 1 ? roles.filter((role) => role !== 'reader') : roles;
  return shown.length ? shown.join(', ') : r.noRoles;
}

/**
 * Writes a value to the clipboard without rendering it (design 5.5 item 8). Call it inside the user gesture:
 * `ClipboardItem` takes the pending value, so the browser keeps the gesture while the request runs. Without
 * `ClipboardItem`, `writeText` follows the response.
 */
export function writeClipboard(value: Promise<string>): Promise<void> {
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (!clipboard) {
    value.catch(() => {});
    return Promise.reject(new ClipboardUnavailableError());
  }
  if (typeof ClipboardItem !== 'undefined' && typeof clipboard.write === 'function') {
    const blob = value.then((text) => new Blob([text], { type: 'text/plain' }));
    blob.catch(() => {});
    return clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
  }
  return value.then((text) => clipboard.writeText(text));
}

/** What the window tells every reveal cell (spec 6.10.18). Null outside a deployment's Variables window. */
export interface RevealEnvironment {
  deploymentId: string;
  /** `/me` lists `reveal-secrets`. */
  canReveal: boolean;
  /** Why Reveal is disabled without the permission. */
  deniedReason: string;
  /** `vault.readIdentity.cloudAccountName`. */
  account: string | null;
  /** The confirmation's session choice is kept per user. */
  user: string;
  /** Changes of the window's deployment, scope or view hide revealed values. */
  scopeKey: string;
}

export const RevealContext = createContext<RevealEnvironment | null>(null);

export function useRevealEnvironment(): RevealEnvironment | null {
  return useContext(RevealContext);
}

/** The location sentence of the confirmation. */
export type RevealLocation = Pick<SettingLocation, 'type' | 'secret' | 'label' | 'container'>;
